// Opening an account by oneself: a name, the phone number, and a password
// typed twice. A username is optional and tucked under "more options" — the
// number is what signs a learner in.
//
// Most learners should never see this page: staff add them by number and hand
// them a password. Registering a number that already has one is answered
// phone_taken, and the page says what to do instead.

import { useId, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { ApiError } from '../api'
import { useAuth } from '../auth'
import { useT } from '../i18n'
import auth from '../i18n/auth'
import { Icon, PhoneInput, SignedOutFrame, messageOf, toE164Uz } from '../lib/ui'
import { AuthBrand, PasswordField } from './SignIn'
import '../styles/auth.css'

// The same rules the server and the database hold, so an obvious mistake is
// answered here rather than after a round trip. They are deliberately no
// stricter than the server's: a form that refuses what the API would accept is
// a bug nobody can see from the outside.
const USERNAME = /^[a-zA-Z0-9._-]{3,32}$/
const USERNAME_MIN = 3
const USERNAME_MAX = 32
const FULL_NAME_MAX = 120
const PASSWORD_MIN = 8
const PASSWORD_MAX = 200
const NATIONAL_DIGITS = 9

type Field = 'full_name' | 'phone' | 'username' | 'password' | 'confirm'
type Errors = Partial<Record<Field, string>>

export function Register() {
  const t = useT(auth)
  const { register } = useAuth()
  const id = useId()

  const [fullName, setFullName] = useState('')
  const [digits, setDigits] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [username, setUsername] = useState('')
  const [more, setMore] = useState(false)

  const [errors, setErrors] = useState<Errors>({})
  // Set when the number turned out to have an account already: the complaint
  // under it then carries a way to the sign-in page.
  const [phoneTaken, setPhoneTaken] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const form = useRef<HTMLFormElement | null>(null)

  /** Everything that can be decided here, reported all at once. */
  function check(): Errors {
    const found: Errors = {}

    const name = fullName.trim()
    if (!name) found.full_name = t('enterFullName')
    else if (count(name) > FULL_NAME_MAX) found.full_name = t('fullNameTooLong', { max: FULL_NAME_MAX })

    if (digits.length === 0) found.phone = t('enterPhone')
    else if (digits.length < NATIONAL_DIGITS) found.phone = t('phoneIncomplete')

    // Counted the way the server counts it — in characters, not in UTF-16 code
    // units — so a passphrase with an emoji in it is measured the same on both
    // sides.
    const length = count(password)
    if (!password) found.password = t('enterPassword')
    else if (length < PASSWORD_MIN || length > PASSWORD_MAX) {
      found.password = t('passwordLength', { min: PASSWORD_MIN, max: PASSWORD_MAX })
    }

    // Checked only here and never sent: a second copy proves nothing to
    // anybody but the person typing it.
    if (!confirm) found.confirm = t('repeatPassword')
    else if (confirm !== password) found.confirm = t('passwordsDiffer')

    const chosen = username.trim()
    if (chosen && !USERNAME.test(chosen)) {
      found.username = t('usernameShape', { min: USERNAME_MIN, max: USERNAME_MAX })
    } else if (chosen && looksLikePhone(chosen)) {
      found.username = t('usernameIsPhone')
    }
    return found
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return

    const found = check()
    setErrors(found)
    setPhoneTaken(false)
    setNotice(null)
    if (Object.keys(found).length > 0) {
      if (found.username) setMore(true)
      focusFirstInvalid()
      return
    }

    setBusy(true)
    try {
      await register({ full_name: fullName, phone: toE164Uz(digits), username, password })
      // No navigation here: once the user is set, App swaps the whole tree.
    } catch (err) {
      const marked = err instanceof ApiError ? serverErrors(err) : {}
      setErrors(marked)
      setPhoneTaken(err instanceof ApiError && err.code === 'phone_taken')
      if (marked.username) setMore(true)
      // A refusal that named no field still has to be said out loud, or the
      // button just stops working.
      setNotice(Object.keys(marked).length > 0 ? null : messageOf(err, auth))
      if (Object.keys(marked).length > 0) focusFirstInvalid()
    } finally {
      setBusy(false)
    }
  }

  /** The server's refusals, in the reader's language and under their own field. */
  function serverErrors(err: ApiError): Errors {
    const out: Errors = {}
    if (err.code === 'phone_taken') {
      out.phone = t('err_phone_taken')
      return out
    }
    if (err.code === 'taken') {
      if (err.fields['username'] !== undefined) out.username = t('usernameTaken')
      if (err.fields['phone'] !== undefined) out.phone = t('err_phone_taken')
      return out
    }
    const f = err.fields
    if (f['full_name'] !== undefined) {
      out.full_name = fullName.trim() ? t('fullNameTooLong', { max: FULL_NAME_MAX }) : t('enterFullName')
    }
    if (f['phone'] !== undefined) out.phone = t('phoneIncomplete')
    if (f['username'] !== undefined) {
      out.username = /phone/i.test(f['username'])
        ? t('usernameIsPhone')
        : t('usernameShape', { min: USERNAME_MIN, max: USERNAME_MAX })
    }
    if (f['password'] !== undefined) out.password = t('passwordLength', { min: PASSWORD_MIN, max: PASSWORD_MAX })
    return out
  }

  // After React has drawn the complaints: the first marked input gets the caret.
  function focusFirstInvalid() {
    window.requestAnimationFrame(() => {
      form.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
    })
  }

  return (
    <SignedOutFrame>
      <div className="login auth">
        <div className="login__card">
          <AuthBrand />
          <h1 className="auth__title">{t('registerTitle')}</h1>
          <p className="auth__lead">{t('registerLead')}</p>

          <section className="notice" aria-labelledby={`${id}-notice`}>
            <h2 id={`${id}-notice`}>{t('notEnrolmentTitle')}</h2>
            <p>{t('notEnrolmentBody')}</p>
          </section>

          <form ref={form} onSubmit={submit} noValidate>
            <div className="auth-field">
              <label className="auth-field__label" htmlFor={`${id}-name`}>
                {t('fullNameLabel')}
              </label>
              <input
                id={`${id}-name`}
                type="text"
                autoComplete="name"
                autoFocus
                maxLength={FULL_NAME_MAX}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                aria-invalid={errors.full_name ? true : undefined}
                aria-describedby={errors.full_name ? `${id}-name-error` : undefined}
              />
              {errors.full_name && (
                <p className="auth-field__error" id={`${id}-name-error`}>
                  {errors.full_name}
                </p>
              )}
            </div>

            <div className="auth-field">
              <label className="auth-field__label" htmlFor={`${id}-phone`}>
                {t('phoneLabel')}
              </label>
              <PhoneInput
                id={`${id}-phone`}
                value={digits}
                onChange={(next) => {
                  setDigits(next)
                  setPhoneTaken(false)
                }}
                invalid={Boolean(errors.phone)}
                label={t('phoneLabel')}
              />
              {errors.phone && (
                // The shared phone field takes no description, so its
                // complaint is announced as it appears instead.
                <p className="auth-field__error" role="alert">
                  {errors.phone}{' '}
                  {phoneTaken && <Link to="/login">{t('signIn')}</Link>}
                </p>
              )}
            </div>

            <PasswordField
              id={`${id}-password`}
              label={t('passwordLabel')}
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              error={errors.password}
              hint={t('passwordHint', { min: PASSWORD_MIN })}
            />

            <PasswordField
              id={`${id}-confirm`}
              label={t('confirmPasswordLabel')}
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              error={errors.confirm}
            />

            <details className="auth__more" open={more} onToggle={(e) => setMore(e.currentTarget.open)}>
              <summary>
                {t('moreOptions')}
                <Icon name="chevronDown" size={16} />
              </summary>
              <div className="auth-field">
                <label className="auth-field__label" htmlFor={`${id}-username`}>
                  {t('usernameOptionalLabel')}
                </label>
                <input
                  id={`${id}-username`}
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={USERNAME_MAX}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  aria-invalid={errors.username ? true : undefined}
                  aria-describedby={errors.username ? `${id}-username-error` : `${id}-username-hint`}
                />
                {errors.username ? (
                  <p className="auth-field__error" id={`${id}-username-error`}>
                    {errors.username}
                  </p>
                ) : (
                  <p className="auth-field__hint" id={`${id}-username-hint`}>
                    {t('usernameOptionalHint', { min: USERNAME_MIN, max: USERNAME_MAX })}
                  </p>
                )}
              </div>
            </details>

            {notice && (
              <p className="auth__alert" role="alert">
                {notice}
              </p>
            )}

            <button type="submit" className="button button--wide" disabled={busy}>
              {busy ? t('creating') : t('createAccount')}
            </button>
          </form>

          <div className="auth__hint">
            <strong>{t('issuedPasswordTitle')}</strong>
            <p>{t('issuedPasswordBody')}</p>
          </div>

          <div className="auth__actions">
            <span className="muted">{t('haveAccount')}</span>
            <Link className="link" to="/login">
              {t('signIn')}
            </Link>
          </div>
        </div>
      </div>
    </SignedOutFrame>
  )
}

function count(s: string): number {
  return [...s].length
}

/**
 * The server's rule, mirrored: a username written with nothing but what phone
 * numbers are written with, holding 9 to 15 digits, would be read as a number
 * at sign-in, so it is refused as a username.
 */
function looksLikePhone(s: string): boolean {
  if (!/^\+?[\d\s().-]+$/.test(s)) return false
  let d = s.replace(/\D/g, '')
  if (d.startsWith('00')) d = d.slice(2)
  return d.length >= 9 && d.length <= 15
}
